import { EntryEditError } from '../edits.ts';
import { javaIsBlank, javaStrip } from '../javaText.ts';
import { int, scalar, text, transaction, type SqlConnection } from '../sql.ts';
import type { Random } from '../dice/random.ts';
import { tableEntry, type ParsedTable, type TableEntry } from './tableCodec.ts';

/**
 * A realm's random tables (docs/PARITY-STEP-2-PLAN.md section 4), read and written as the
 * desktop's `RandomTableRepository` and `RandomTableService` do: a table's rows are its whole
 * list, replaced together; a blank name is "Untitled Table"; rolling picks by weight, as a
 * die the size of the total weight would.
 */

export interface TableSummary {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly entryCount: number;
  readonly modified: string;
}

export interface RandomTable {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly entries: readonly TableEntry[];
  readonly created: string;
  readonly modified: string;
}

export const TABLE_NAME_MAX = 200;
export const TABLE_DESCRIPTION_MAX = 4000;
export const TABLE_RESULT_MAX = 2000;
export const TABLE_ROWS_MAX = 10_000;
/** The desktop's weight spinner goes up to a million. */
export const TABLE_WEIGHT_MAX = 1_000_000;

/** In the desktop's gallery order: by name, without regard to case. */
export function listTables(conn: SqlConnection): TableSummary[] {
  return conn
    .all(
      `SELECT t.id, t.name, t.description, t.modified,
              (SELECT COUNT(*) FROM random_table_entries e WHERE e.table_id = t.id) AS entry_count
         FROM random_tables t ORDER BY t.name COLLATE NOCASE`,
    )
    .map((row) => ({ id: text(row['id']), name: text(row['name']), description: text(row['description']), entryCount: int(row['entry_count']), modified: text(row['modified']) }));
}

export function getTable(conn: SqlConnection, id: string): RandomTable | null {
  const row = conn.all('SELECT id, name, description, created, modified FROM random_tables WHERE id = ?', [id])[0];
  if (!row) return null;
  const entries = conn
    .all('SELECT text, weight FROM random_table_entries WHERE table_id = ? ORDER BY position', [id])
    .map((r) => tableEntry(text(r['text']), int(r['weight'])));
  return { id, name: text(row['name']), description: text(row['description']), entries, created: text(row['created']), modified: text(row['modified']) };
}

function cleanName(name: string): string {
  const flat = name.replace(/[\r\n]+/g, ' ');
  if (flat.length > TABLE_NAME_MAX) throw new EntryEditError('invalid', `A table's name can be at most ${TABLE_NAME_MAX} characters.`);
  return javaIsBlank(flat) ? 'Untitled Table' : javaStrip(flat);
}

function cleanDescription(description: string): string {
  if (description.length > TABLE_DESCRIPTION_MAX) throw new EntryEditError('invalid', `A description can be at most ${TABLE_DESCRIPTION_MAX} characters.`);
  return description;
}

function cleanEntries(entries: readonly TableEntry[]): TableEntry[] {
  if (entries.length > TABLE_ROWS_MAX) throw new EntryEditError('invalid', `A table can have at most ${TABLE_ROWS_MAX.toLocaleString('en')} results.`);
  return entries.map((entry) => {
    if (entry.text.length > TABLE_RESULT_MAX) throw new EntryEditError('invalid', `A result can be at most ${TABLE_RESULT_MAX} characters.`);
    if (!Number.isInteger(entry.weight) || entry.weight > TABLE_WEIGHT_MAX) throw new EntryEditError('invalid', `A weight must be a whole number from 1 to ${TABLE_WEIGHT_MAX.toLocaleString('en')}.`);
    return tableEntry(entry.text, entry.weight);
  });
}

function writeEntries(conn: SqlConnection, tableId: string, entries: readonly TableEntry[]): void {
  conn.run('DELETE FROM random_table_entries WHERE table_id = ?', [tableId]);
  entries.forEach((entry, i) => conn.run('INSERT INTO random_table_entries (table_id, position, text, weight) VALUES (?, ?, ?, ?)', [tableId, i, entry.text, entry.weight]));
}

export function createTable(conn: SqlConnection, name: string, description = '', entries: readonly TableEntry[] = [], id: string = crypto.randomUUID(), now: string = new Date().toISOString()): string {
  const clean = { name: cleanName(name), description: cleanDescription(description), entries: cleanEntries(entries) };
  transaction(conn, () => {
    conn.run('INSERT INTO random_tables (name, description, created, modified, id) VALUES (?, ?, ?, ?, ?)', [clean.name, clean.description, now, now, id]);
    writeEntries(conn, id, clean.entries);
  });
  return id;
}

/** Saves a table's name, description and whole list of rows, as the desktop's `updateTable`. */
export function updateTable(conn: SqlConnection, id: string, name: string, description: string, entries: readonly TableEntry[], now: string = new Date().toISOString()): void {
  const clean = { name: cleanName(name), description: cleanDescription(description), entries: cleanEntries(entries) };
  transaction(conn, () => {
    if (int(scalar(conn, 'SELECT COUNT(*) FROM random_tables WHERE id = ?', [id])) === 0) throw new EntryEditError('not_found', 'That table no longer exists.');
    conn.run('UPDATE random_tables SET name = ?, description = ?, modified = ? WHERE id = ?', [clean.name, clean.description, now, id]);
    writeEntries(conn, id, clean.entries);
  });
}

/** A new table from an import; a name already used gets a number, "Loot (2)", as the desktop's `importTable`. */
export function importTable(conn: SqlConnection, parsed: ParsedTable, id?: string, now?: string): string {
  const base = javaIsBlank(parsed.name) ? 'Imported Table' : javaStrip(parsed.name);
  const taken = new Set(listTables(conn).map((t) => t.name.toLowerCase()));
  let candidate = base;
  for (let n = 2; taken.has(candidate.toLowerCase()); n++) candidate = `${base} (${n})`;
  return createTable(conn, candidate, parsed.description, parsed.entries, id, now);
}

export function deleteTable(conn: SqlConnection, id: string): void {
  transaction(conn, () => {
    if (int(scalar(conn, 'SELECT COUNT(*) FROM random_tables WHERE id = ?', [id])) === 0) throw new EntryEditError('not_found', 'That table no longer exists.');
    conn.run('DELETE FROM random_tables WHERE id = ?', [id]);
  });
}

export interface TableRoll {
  readonly entry: TableEntry;
  /** The number the die showed... */
  readonly roll: number;
  /** ...on a die as big as the total weight. */
  readonly die: number;
}

/** `RandomTableService.rollOnDie`: a weighted pick, as a die the size of the total weight; null for no rows. */
export function rollTable(entries: readonly TableEntry[], random: Random): TableRoll | null {
  if (entries.length === 0) return null;
  const totalWeight = entries.reduce((sum, entry) => sum + entry.weight, 0);
  const pick = random.nextInt(totalWeight);
  let cumulative = 0;
  for (const entry of entries) {
    cumulative += entry.weight;
    if (pick < cumulative) return { entry, roll: pick + 1, die: totalWeight };
  }
  return { entry: entries.at(-1)!, roll: totalWeight, die: totalWeight };
}
